import { ChangeEvent, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { Alert } from 'us-react-components/dist/Alert/Alert';
import { Input } from 'us-react-components/dist/Input/Input';
import { UserService, ValidationService } from 'us-web-services';

import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { Storage } from '../../../constants/Storage';
import { useDependencies } from '../../../dependencies';
import { Routes } from '../../../Routes';

export const Confirm = observer((): JSX.Element => {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { stores: { familyRegistration }, services: { tracker }, stores } = useDependencies();
  const { user } = stores.user;

  const [confirmedFirstName, setConfirmedFirstName] = useState(user?.firstName);
  const [confirmedLastName, setConfirmedLastName] = useState(user?.lastName);
  const [confirmedEmail, setConfirmedEmail] = useState(user?.email);
  const [continuing, setContinuing] = useState(false);
  const [validation, setValidation] = useState({
    firstName: ValidationService.isName(user?.firstName ?? ''),
    lastName: ValidationService.isName(user?.lastName ?? ''),
    email: ValidationService.isValidEmail(user?.email ?? ''),
  });

  const labelClasses = 'text-white peer-focus:text-white peer-placeholder-shown:text-white peer-placeholder-shown:top-0 peer-placeholder-shown:text-sm';

  const redirect = async (): Promise<void> => {
    const redirectTo = cookies.get(Storage.REDIRECT_URL);
    const isSitterRedirect = /^\/app\/sitter\/\d+\/recommend/.test(redirectTo) || /^\/sitter\/\d+\/recommend/.test(redirectTo);

    let nextPath;

    if (localStorage.getItem(Storage.COMPANY_EMPLOYEE) || localStorage.getItem(Storage.COMPANY_EMAIL)) {
      nextPath = Routes.parent.benefits.confirm;
    } else if (isSitterRedirect) {
      nextPath = redirectTo.replace(/^\/app\//, '/');
      cookies.remove(Storage.REDIRECT_URL, { path: '/' });
    } else {
      nextPath = Routes.parent.registration.gettingStarted;
    }

    navigate(nextPath);
  }

  const isValid = (): boolean => validation.firstName && validation.lastName && validation.email;

  const onError = (trackingProps: Record<string, string> = {}): void => {
    toast.error(t('pages.nux.family.confirm.error'));
    tracker.track('user-error', trackingProps);
  }

  const onContinue = async (): Promise<void> => {
    const trackingProps = {
      elementId: 'family-nux-social-confirm',
      elementText: t('pages.nux.family.confirm.continue')
    };

    try {
      setContinuing(true);

      if (user.id) {
        await UserService.patch(user.id, {
          id: user.id,
          firstName: confirmedFirstName,
          lastName: confirmedLastName,
          email: confirmedEmail,
        });

        await stores.user.refreshUser();

        cookies.set(Storage.SOCIAL_CONFIRMED, true, {
          path: '/',
        });

        tracker.track('clicked-button', trackingProps);

        redirect();
      } else {
        onError(trackingProps);
      }
    } catch {
      onError(trackingProps);
    } finally {
      setContinuing(false);
    }
  }

  const onFirstNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setConfirmedFirstName(event.target.value);

    setValidation({
      ...validation,
      firstName: ValidationService.isName(event.target.value),
    });
  }

  const onLastNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setConfirmedLastName(event.target.value);

    setValidation({
      ...validation,
      lastName: ValidationService.isName(event.target.value),
    });
  }

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setConfirmedEmail(event.target.value);

    setValidation({
      ...validation,
      email: ValidationService.isValidEmail(event.target.value),
    });
  }

  return (
    <NuxContainer
      inverted
      hideProgress
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      title={t('pages.nux.family.confirm.title')}
      continueTitle={t('pages.nux.family.confirm.continue')}
      isContinueDisabled={!isValid() || continuing}
      onContinue={onContinue}
    >
      <div className='px-10 md:pt-5 justify-between'>
        <div className='text-center text-white md:text-xl font-bold'>
          {t('pages.nux.family.confirm.legalName')}
        </div>
        <div className='flex flex-wrap justify-between md:mx-10'>
          <div className='w-full md:w-[47%]'>
            <Input id='firstName' className='mt-5 w-full' labelClasses={labelClasses} name='firstName' type='text' label={t('pages.nux.family.confirm.firstName')} value={confirmedFirstName} onChange={onFirstNameChange} />
            <Alert className='w-full' color='error' isOpen={!confirmedFirstName}>
              {t('pages.nux.family.confirm.firstName')} {t('validation.errors.isRequired')}
            </Alert>
            <Alert className='w-full' color='error' isOpen={!!confirmedFirstName && !validation.firstName}>
              {t('validation.errors.noNumbersSymbols')}
            </Alert>
          </div>
          <div className='w-full md:w-[47%]'>
            <Input id='lastName' className='mt-5 w-full' labelClasses={labelClasses} name='lastName' type='text' label={t('pages.nux.family.confirm.lastName')} value={confirmedLastName} onChange={onLastNameChange} />
            <Alert className='w-full' color='error' isOpen={!confirmedLastName}>
              {t('pages.nux.family.confirm.lastName')} {t('validation.errors.isRequired')}
            </Alert>
            <Alert className='w-full' color='error' isOpen={!!confirmedLastName && !validation.lastName}>
              {t('validation.errors.noNumbersSymbols')}
            </Alert>
          </div>
        </div>
        <div className='mt-14 mb-3 text-center text-white md:text-xl font-bold'>
          {t('pages.nux.family.confirm.emailToUse')}
        </div>
        <Input id='email' className='mx-auto md:w-[50%]' labelClasses={labelClasses} inputMode='email' name='email' type='text' label={t('pages.nux.family.confirm.email')} value={confirmedEmail} onChange={onEmailChange} />
        <Alert className='mx-auto md:w-[50%]' color='error' isOpen={!validation.email}>
          {t('validation.errors.email')}
        </Alert>
      </div>
    </NuxContainer>
  );
});
